import apiClient from '../../../shared/lib/api'
import type { VersionChanges } from '../types'

/** Shape of an Axios response wrapping a Go API { data: T } envelope */
interface ApiResponse<T = Record<string, string | number | boolean | null | object | Array<object>>> {
  data: { data?: T } | T
}

/** Unwrap Go API { data: T } envelope (API already returns camelCase) */
function unwrap<T = Record<string, string | number | boolean | null | object | Array<object>>>(
  response: ApiResponse<T>,
): T {
  const body = response.data as Record<string, T>
  return (body?.data ?? body) as T
}

export interface Funnel {
  id: string
  name: string
  description?: string
  isActive: boolean
  isDefault: boolean
  priority: number
  funnelType?: string
  triggerType?: string
  steps?: FunnelStep[]
  createdAt: string
  updatedAt: string
}

export interface FunnelStep {
  id: string
  name?: string
  stepType: 'upsell' | 'downsell' | 'checkout'
  position: number
  offerId?: string
  offer?: Offer
  pageContent?: string
  experienceType?: string
  placement?: string
  nextStepAccept?: string
  nextStepDecline?: string
  createdAt?: string
  updatedAt?: string
}

export interface Offer {
  id: string
  name: string
  description?: string
  productId?: string
  headline?: string
  imageUrl?: string
  ctaText?: string
  declineText?: string
  discountConfig?: { valueType: string; value: number; maxDiscount?: number | null }
  originalPrice?: { base: number; display: number }
  discountedPrice?: { base: number; display: number }
  discountType?: 'percentage' | 'fixed' | 'bogo'
  discountValue?: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// Funnel endpoints (no /admin prefix - routes are /funnels and /offers)
const FUNNELS_BASE = '/funnels'
const OFFERS_BASE = '/offers'

export const funnelApi = {
  list: async (): Promise<{ data: Funnel[] }> => {
    const response = await apiClient.get(FUNNELS_BASE)
    const inner = unwrap(response)
    return { data: inner.funnels || inner || [] }
  },
  get: async (id: string): Promise<{ data: Funnel }> => {
    const response = await apiClient.get(`${FUNNELS_BASE}/${id}`)
    const inner = unwrap(response)
    return { data: inner.funnel || inner }
  },
  create: (data: { name: string; description?: string; isActive?: boolean; priority?: number }) =>
    apiClient.post(FUNNELS_BASE, data),
  update: (id: string, data: { name?: string; description?: string; isActive?: boolean; priority?: number }) =>
    apiClient.put(`${FUNNELS_BASE}/${id}`, data),
  delete: (id: string) => apiClient.delete(`${FUNNELS_BASE}/${id}`),
  duplicate: (id: string) => apiClient.post(`${FUNNELS_BASE}/${id}/duplicate`),
  getStep: async (funnelId: string, stepId: string): Promise<{ data: FunnelStep }> => {
    const response = await apiClient.get(`${FUNNELS_BASE}/${funnelId}/steps/${stepId}`)
    const inner = unwrap(response)
    return { data: inner.step || inner }
  },
  listSteps: async (funnelId: string): Promise<{ data: FunnelStep[] }> => {
    const response = await apiClient.get(`${FUNNELS_BASE}/${funnelId}/steps`)
    const inner = unwrap(response)
    return { data: inner.steps || inner || [] }
  },
  createStep: (funnelId: string, data: { name: string; stepType: string; position: number; offerId?: string }) =>
    apiClient.post(`${FUNNELS_BASE}/${funnelId}/steps`, data),
  updateStep: (funnelId: string, stepId: string, data: { name?: string; stepType?: string; position?: number; offerId?: string; pageContent?: string }) =>
    apiClient.put(`${FUNNELS_BASE}/${funnelId}/steps/${stepId}`, data),
  deleteStep: (funnelId: string, stepId: string) =>
    apiClient.delete(`${FUNNELS_BASE}/${funnelId}/steps/${stepId}`),
}

export interface ChangelogEntry {
  id: string
  entityType: string
  entityId: string
  action: string
  changes?: string | VersionChanges
  actor: string
  createdAt: string
}

export interface AlertRule {
  id: string
  name: string
  description?: string
  entityType: string
  triggerAction: string
  severity: 'info' | 'warning' | 'critical'
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface AlertEvent {
  id: string
  ruleId: string
  changelogEntryId: string
  severity: 'info' | 'warning' | 'critical'
  message: string
  isRead: boolean
  createdAt: string
}

export const offerApi = {
  list: async (): Promise<{ data: Offer[] }> => {
    const response = await apiClient.get(OFFERS_BASE)
    const inner = unwrap(response)
    return { data: inner.offers || inner || [] }
  },
  get: async (id: string): Promise<{ data: Offer }> => {
    const response = await apiClient.get(`${OFFERS_BASE}/${id}`)
    const inner = unwrap(response)
    return { data: inner.offer || inner }
  },
  create: (data: { name: string; description?: string; discountType: string; discountValue: number; isActive?: boolean }) =>
    apiClient.post(OFFERS_BASE, data),
  update: (id: string, data: { name?: string; description?: string; discountType?: string; discountValue?: number; isActive?: boolean }) =>
    apiClient.put(`${OFFERS_BASE}/${id}`, data),
  delete: (id: string) => apiClient.delete(`${OFFERS_BASE}/${id}`),
}

// Changelog API
export const changelogApi = {
  list: async (params?: { entityType?: string; action?: string; from?: string; to?: string }): Promise<{ data: ChangelogEntry[] }> => {
    const response = await apiClient.get('/changelog', { params })
    const inner = unwrap(response)
    return { data: inner.entries || inner || [] }
  },
}

// Alerts API
export const alertsApi = {
  listRules: async (): Promise<{ data: AlertRule[] }> => {
    const response = await apiClient.get('/alerts/rules')
    const inner = unwrap(response)
    return { data: inner.rules || inner || [] }
  },
  createRule: (data: { name: string; entityType: string; triggerAction: string; severity: string }) =>
    apiClient.post('/alerts/rules', data),
  updateRule: (id: string, data: { name?: string; entityType?: string; triggerAction?: string; severity?: string; isActive?: boolean }) =>
    apiClient.put(`/alerts/rules/${id}`, data),
  deleteRule: (id: string) => apiClient.delete(`/alerts/rules/${id}`),
  listEvents: async (params?: { severity?: string; isRead?: string }): Promise<{ data: AlertEvent[] }> => {
    const response = await apiClient.get('/alerts/events', { params })
    const inner = unwrap(response)
    return { data: inner.events || inner || [] }
  },
  markRead: (id: string) => apiClient.post(`/alerts/events/${id}/read`),
  unreadCount: async (): Promise<{ data: { count: number } }> => {
    const response = await apiClient.get('/alerts/events/unread/count')
    return { data: unwrap(response) }
  },
}
